<template>
  <v-row
    justify="space-around"
  >
    <v-col
      cols="12"
      :lg="gridSpacing.lg"
      :md="gridSpacing.md"
      :sm="gridSpacing.sm"
    >
      <!-- Médaillon Nombre de collaborateurs -->
      <stats-template
        :is-loading="isLoading.users"
        :has-error="hasError.users"
        :error-msg="errorMsg"
        icon="mdi-compass"
        :title="$t(title.users)"
        :value="numberOfUsers"
        :color="color"
        :small-height="true"
      />
    </v-col>
    <v-col
      cols="12"
      :lg="gridSpacing.lg"
      :md="gridSpacing.md"
      :sm="gridSpacing.sm"
    >
      <!-- Médaillon Nombre d'apprentissage -->
      <stats-template
        :is-loading="isLoading.learnings"
        :has-error="hasError.learnings"
        :error-msg="errorMsg"
        icon="mdi-school"
        :title="$t(title.learnings)"
        :value="numberOfLearnings"
        :color="color"
        :small-height="true"
      />
    </v-col>
    <v-col
      cols="12"
      :lg="gridSpacing.lg"
      :md="gridSpacing.md"
      :sm="gridSpacing.sm"
    >
      <!-- Médaillon Taux de participation -->
      <stats-template
        :is-loading="isLoading.participation"
        :has-error="hasError.participation"
        :error-msg="errorMsg"
        icon="mdi-equalizer"
        :title="$t(title.participation)"
        :value="participationRate"
        :small-value="participationRate && participationRate !== 0 ? '%' : ''"
        :color="color"
        :small-height="true"
      />
    </v-col>
  </v-row>
</template>

<script>
  /* Les composants et packages dont on a besoin */
  import StatsTemplate from './templates/StatsTemplate.vue'

  /* Rend le composant accessible aux autres composants */
  export default {
    /* Nom du composant */
    name: 'StatsTraining',

    /* Sous-composants utilisés par ce composant */
    components: {
      StatsTemplate,
    },

    /* Paramètres */
    props: {
      request: {
        type: String,
        default: '',
      },
      isAdmin: {
        type: Boolean,
        default: false,
      },
      errorMsg: {
        type: String,
        default: 'Erreur',
      },
      gridSpacing: {
        type: Object,
        default: function () {
          return {
            xl: '4',
            lg: '4',
            md: '6',
            sm: '12',
            xs: '12',
          }
        },
      },
      color: {
        type: String,
        default: 'primary',
      },
    },

    /* Variables internes
     * data doit être une fonction si ce composant est utilisé dans un autre composant */
    data: function () {
      return {
        title: {
          users: 'Stats.Training.Users.Title',
          learnings: 'Stats.Training.Learnings.Title',
          participation: 'Stats.Training.Participation.Title',
        },
        numberOfUsers: undefined,
        numberOfInactiveUsers: undefined,
        numberOfLearnings: undefined,
        participationRate: undefined,
        isLoading: {
          users: true,
          inactiveUsers: true,
          learnings: true,
          participation: true,
        },
        hasError: {
          users: false,
          inactiveUsers: false,
          learnings: false,
          participation: false,
        },
      }
    },

    /* Variables internes dynamiques
     * (MAJ automatique quand les variables qu'elles utilisent sont mises à jour) */
    computed: {
      numberOfInactiveUsersString: function () {
        const str = 'Stats.Training.Users.Subtext'
        return (this.numberOfInactiveUsers
          ? this.$tc(str, this.numberOfInactiveUsers)
          : this.$tc(str, 1, { n: 0 }))
      },
    },

    /* Variables surveillées : traitement si modification de sa valeur */
    watch: {
      request: function (newVal, oldVal) {
        this.fetchAllStats()
      },
    },

    /* Hook : fonction appelée juste après que l’instance ait été montée */
    mounted: function () {
      this.fetchAllStats()
    },

    /* Fonctions internes */
    methods: {
      fetchAllStats: function () {
        this.isLoading.users = true
        this.isLoading.inactiveUsers = true
        this.isLoading.learnings = true
        this.isLoading.participation = true
        this.fetchNumberOfUsers()
        this.fetchNumberOfInactiveUsers()
        this.fetchNumberOfLearnings()
        this.fetchParticipationRate()
      },

      // Récupération des données statistiques (Taux de participation)
      fetchParticipationRate: function () {
        this.$Auth.httpRequest(
          'get',
          'api/dashboard/numberParticipationRate' + this.request,
        )
          .then(datas => {
            this.participationRate = Number(datas.data[0].rate)
          })
          .catch((error) => {
            this.participationRate = undefined
            this.hasError.participation = true
            if (this.isAdmin) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
            this.$emit('set-error', error)
          })
          .finally(() => {
            this.isLoading.participation = false
          })
      },

      // Récupération des données statistiques (Nombre d'apprentissage)
      fetchNumberOfLearnings: function () {
        this.$Auth.httpRequest(
          'get',
          'api/dashboard/count/selectNumberOfLearningsCount' + this.request,
        )
          .then(datas => {
            this.numberOfLearnings = datas.data[0].count
          })
          .catch((error) => {
            this.numberOfLearnings = undefined
            this.hasError.learnings = true
            if (this.isAdmin) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
            this.$emit('set-error', error)
          })
          .finally(() => {
            this.isLoading.learnings = false
          })
      },

      // Récupération des données statistiques (Collaborateurs en quête)
      fetchNumberOfUsers: function () {
        this.$Auth.httpRequest(
          'get',
          'api/dashboard/numberOfUsers' + this.request,
        )
          .then(datas => {
            this.numberOfUsers = datas.data[0].numberOfUsers
          })
          .catch((error) => {
            this.numberOfUsers = undefined
            this.hasError.users = true
            if (this.isAdmin) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
            this.$emit('set-error', error)
          })
          .finally(() => {
            this.isLoading.users = false
          })
      },

      fetchNumberOfInactiveUsers: function () {
        this.$Auth.httpRequest(
          'get',
          'api/dashboard/count/selectInactives' + this.request,
        )
          .then(datas => {
            this.numberOfInactiveUsers = datas.data[0].count
          })
          .catch((error) => {
            this.numberOfInactiveUsers = undefined
            this.hasError.inactiveUsers = true
            if (this.isAdmin) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
            this.$emit('set-error', error)
          })
          .finally(() => {
            this.isLoading.inactiveUsers = false
          })
      },
    },
  }
</script>
